import {useContext, useEffect, useMemo} from "react";
import {observer} from "mobx-react";
import {CalendarSmall, YStack, Loader} from "@chayka/ui";
import {RoomsSlider, HoursButtons} from "@/components";
import {BookingStateContext} from "../state";
import type {BookingWizardSlideComponent} from "./types";
import {trpc} from "@/app/trpc";
import {useQuery} from "@tanstack/react-query";
import {MAX_DATE, MIN_DATE} from "../constants";

export const RoomTimeSlide: BookingWizardSlideComponent = observer(({setNextDisabled}) => {
  const {subType, date, setDate, room, setRoom, hours, setHours, queryRoomId} =
    useContext(BookingStateContext);

  if (!subType) {
    throw new Error("Не выбран тип комнаты");
  }

  const {data: rooms, isLoading: roomsLoading} = useQuery(
    trpc.booking.roomsBySubType.queryOptions({subTypeCode: subType.code}),
  );

  const visibleRooms = useMemo(() => {
    if (!rooms?.length) {
      return [];
    }

    const preselectedRoom = rooms.find((candidate) => candidate.id === queryRoomId);

    return preselectedRoom ? [preselectedRoom] : rooms;
  }, [queryRoomId, rooms]);

  useEffect(() => {
    if (!visibleRooms.length) {
      setRoom(null);
      return;
    }

    const nextRoom =
      visibleRooms.find((candidate) => candidate.id === queryRoomId) ||
      visibleRooms.find((candidate) => candidate.id === room?.id) ||
      visibleRooms[0];

    if (nextRoom && nextRoom.id !== room?.id) {
      setRoom(nextRoom);
    }
  }, [queryRoomId, room?.id, setRoom, visibleRooms]);

  const {data: scheduleData, isLoading: scheduleLoading} = useQuery({
    ...trpc.booking.roomSchedule.queryOptions({roomId: room?.id || -1, date}),
    enabled: Boolean(room?.id),
  });

  useEffect(() => {
    setNextDisabled(!hours.length);
  }, [hours, setNextDisabled]);

  if (roomsLoading) {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <YStack className="min-h-0 flex-1 gap-6">
      <div className="shrink-0">
        <RoomsSlider rooms={visibleRooms} selectedRoom={room} onSelectRoom={setRoom} />
      </div>

      <div className="shrink-0">
        <CalendarSmall minDate={MIN_DATE} maxDate={MAX_DATE} value={date} onChange={setDate} />
      </div>

      {scheduleLoading && (
        <div className="h-32 w-full flex justify-center items-center">
          <Loader />
        </div>
      )}

      {!scheduleLoading && (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-26 -mt-6 no-scrollbar">
          <div className="mt-6">
            <HoursButtons
              schedule={scheduleData?.timeSlots || []}
              selectedHours={hours}
              setHours={setHours}
              nightHoursTogether={room?.nightHoursTogether}
            />
          </div>
        </div>
      )}
    </YStack>
  );
});
